<template>
  <div class="home-page">
    <!-- Loading Overlay -->
    <LoadingOverlay
      :loading="loading"
      :title="loadingTitle"
      :subtitle="loadingSubtitle"
    />

    <!-- Fixed Header Section -->
    <section class="header-section">
      <!-- Hero Banner -->
      <div class="hero-section">
        <div class="container-fluid px-0">
          <!-- Always show skeleton until data loads -->
          <SkeletonBanner v-if="!dataLoaded.banner" />
          <BannerSlide
            v-else
            :auto-play="true"
            :interval="6000"
            :show-indicators="true"
            @slide-start="onSlideStart"
            @slide-end="onSlideEnd"
          />
        </div>
      </div>

      <!-- Game Menu -->
      <div class="game-menu-section">
        <div class="container-fluid px-0">
          <!-- Always show skeleton until data loads -->
          <SkeletonGameMenu v-if="!dataLoaded.menu" />
          <GameMenu
            v-else
            :selected-key-name="selectedKeyName"
            @selection-change="handleSelectionChange"
          />
        </div>
      </div>
    </section>

    <!-- Dynamic Content Section - COMMENTED FOR PERFORMANCE TEST -->
    <section class="content-section">
      <div class="container-fluid px-0">
        <transition name="content-fade" mode="out-in">
          <div
            v-if="activeMenu === 'homepage'"
            key="homepage"
            class="homepage-content"
          >
            <!-- Always show skeleton until data loads -->
            <template v-if="!dataLoaded.homepageGames">
              <SkeletonHorizontalList />
              <SkeletonHorizontalList />
              <SkeletonHorizontalList />
              <SkeletonHorizontalList />
            </template>
            <template v-else>
              <HorizontalGameLists
                title="👤 5เกมส์แตก สำหรับยูสใหม่"
                :games="randomNewUserGames"
                :slider-loop="false"
                :show-online-count="true"
                :show-percentage="true"
                @game-select="handleGameSelect"
                @show-login-modal="$emit('show-login-modal')"
              />
              <HorizontalGameLists
                title="🔥 เกมส์เข้าใหม่ ใครก็ว่าแจก"
                :games="randomNewGames"
                :slider-loop="false"
                :show-online-count="true"
                @game-select="handleGameSelect"
                @show-login-modal="$emit('show-login-modal')"
              />
              <HorizontalGameLists
                title="🌟 เกมส์ฮิต PG ค่ายมหาชน"
                :games="randomHotPGGames"
                :slider-loop="false"
                :show-online-count="true"
                :show-percentage="true"
                @game-select="handleGameSelect"
                @show-login-modal="$emit('show-login-modal')"
              />
              <HorizontalGameLists
                title="🎁 เกมส์แตกหนัก"
                :games="randomBonusGames"
                :slider-loop="false"
                :show-percentage="true"
                @game-select="handleGameSelect"
                @show-login-modal="$emit('show-login-modal')"
              />
            </template>
          </div>

          <!-- Game Content (replaces both category and gameType) -->
          <div
            v-else-if="activeMenu === 'gameSelected'"
            :key="`gameSelected-${selectedKeyName}`"
            class="game-content"
          >
            <component
              :is="currentComponent"
              v-bind="currentComponentProps"
              @game-select="handleGameSelect"
              @provider-selected="handleProviderSelect"
              @view-state-change="handleProviderViewStateChange"
              @navigate-home="handleNavigateHome"
              @show-login-modal="$emit('show-login-modal')"
            />
          </div>
        </transition>
      </div>
    </section>
  </div>
</template>

<script>
import LoadingOverlay from '@/components/LoadingOverlay.vue'
import { gameAPI } from '@/services/gameAPI'
import { GameDataProcessor } from '@/services/gameDataProcessor'
import { CacheStrategyFactory } from '@/services/gameCacheStrategy'
import { setPromoId, isUserLoggedIn } from '@/auth/utils'
import {
  LOADING_MESSAGES,
  HOMEPAGE_SECTIONS,
  MENU_CACHE_KEYS,
  CONTENT_CONFIG,
  MENU_TITLES
} from '@/constants/gameConstants'
import BannerSlide from './component/BannerSlide.vue'
import GameMenu from './component/GameMenu.vue'
import HorizontalGameLists from './component/HorizontalGameLists.vue'
import GameGridContent from './component/GameGridContent.vue'
import ProviderLists from './component/ProviderLists.vue'
import Lottery from './component/Lottery.vue'

// Skeleton Components
import SkeletonBanner from './component/skeleton/SkeletonBanner.vue'
import SkeletonGameMenu from './component/skeleton/SkeletonGameMenu.vue'
import SkeletonHorizontalList from './component/skeleton/SkeletonHorizontalList.vue'

export default {
  name: 'HomePage',
  components: {
    BannerSlide,
    GameMenu,
    HorizontalGameLists,
    GameGridContent,
    ProviderLists,
    Lottery,
    LoadingOverlay,
    // Skeleton Components
    SkeletonBanner,
    SkeletonGameMenu,
    SkeletonHorizontalList
  },
  emits: ['show-login-modal'],
  data() {
    return {
      // Navigation state
      currentSlide: 0,
      selectedKeyName: null, // Current selected keyName
      activeMenu: 'homepage', // 'homepage' | 'gameSelected'

      // Provider view state tracking for header management
      providerViewState: 'providers', // 'providers' | 'games'

      // Loading states
      loading: false,
      loadingTitle: 'กำลังโหลด...',
      loadingSubtitle: 'กรุณารอสักครู่',

      // Skeleton loading states
      showSkeletonUI: true,
      dataLoaded: {
        banner: false,
        menu: false,
        homepageGames: false
      },
      // Timing control for skeleton display
      skeletonStartTime: null,
      minimumSkeletonTime: 500, // Show skeleton for at least 500ms

      // Data
      homepageGames: {
        bonusGames: [],
        newGames: [],
        newUserGames: [],
        hotPGGames: [],
        popularGames: [],
        // Unlimited versions for menu pages
        newGamesUnlimited: [],
        bonusGamesUnlimited: [],
        popularGamesUnlimited: []
      },
      currentContent: [],
      selectedMenuItem: null,

      // Real API data - unified providers storage
      providers: {
        slots: [],
        baccarat: [],
        sport: [],
        lottery: []
      },

      // Search state
      searchQuery: '',

      // Smart caching system by keyName
      cache: {
        homepage: null
      },

      // Cache strategy instances
      cacheStrategies: {}
    }
  },
  computed: {
    // Determine which component to use based on selectedKeyName
    currentComponent() {
      return CONTENT_CONFIG[this.selectedKeyName] || 'GameGridContent'
    },

    // Get current providers based on selected section
    currentProviders() {
      return this.providers[this.selectedKeyName] || []
    },

    // Search functionality
    filteredContent() {
      if (!this.searchQuery.trim()) {
        return this.currentComponent === 'ProviderLists'
          ? this.currentProviders
          : this.currentContent
      }

      const query = this.searchQuery.toLowerCase()

      if (this.currentComponent === 'ProviderLists') {
        return this.currentProviders.filter(provider =>
          provider.providerName.toLowerCase().includes(query)
        )
      }
      return this.currentContent.filter(
        game => game.gameName && game.gameName.toLowerCase().includes(query)
      )
    },

    // Search state helpers
    searchActive() {
      return this.searchQuery.trim().length > 0
    },

    filteredContentCount() {
      return this.filteredContent.length
    },

    totalContentCount() {
      return this.currentComponent === 'ProviderLists'
        ? this.providerData.length
        : this.currentContent.length
    },

    // Get current content count for subtitle
    currentContentCount() {
      if (this.currentComponent === 'ProviderLists') {
        return this.currentProviders.length
      }
      return this.currentContent.length
    },

    // Get appropriate unit text for subtitle
    currentContentUnit() {
      if (this.currentComponent === 'ProviderLists') {
        return 'ผู้ให้บริการ'
      }
      return 'เกมส์'
    },

    // Random 5 games for each section
    randomNewUserGames() {
      return this.getRandomItems(this.homepageGames.newUserGames, 5)
    },
    randomNewGames() {
      return this.getRandomItems(this.homepageGames.newGames, 5)
    },
    randomHotPGGames() {
      return this.getRandomItems(this.homepageGames.hotPGGames, 5)
    },
    randomBonusGames() {
      return this.getRandomItems(this.homepageGames.bonusGames, 5)
    },

    // Get props for current component
    currentComponentProps() {
      if (this.currentComponent === 'ProviderLists') {
        return {
          providers: this.currentProviders,
          sectionTitle: this.selectedMenuItem?.title
        }
      }
      // Handle different props for specific keyNames
      if (this.selectedKeyName === 'new-games') {
        return {
          games: this.filteredContent,
          loading: this.loading,
          showOnlineCount: true,
          showPercentage: false,
          menuTitle: this.selectedMenuItem?.title || 'เกมส์'
        }
      }

      if (this.selectedKeyName === 'hot-games') {
        return {
          games: this.filteredContent,
          loading: this.loading,
          showOnlineCount: true,
          showPercentage: true,
          menuTitle: this.selectedMenuItem?.title || 'เกมส์'
        }
      }

      if (this.selectedKeyName === 'popular-games') {
        return {
          games: this.filteredContent,
          loading: this.loading,
          showOnlineCount: true,
          showPercentage: false,
          menuTitle: this.selectedMenuItem?.title || 'เกมส์'
        }
      }

      return {
        games: this.filteredContent,
        loading: this.loading,
        showOnlineCount: true,
        menuTitle: this.selectedMenuItem?.title || 'เกมส์'
      }
    }
  },
  created() {
    // Initialize skeleton UI immediately
    console.log('HomePage: created - initializing skeleton UI')
    this.showSkeletonUI = true
    this.skeletonStartTime = Date.now()
  },
  beforeMount() {
    // Component is about to mount
    console.log('HomePage: beforeMount - component starting')
  },
  async mounted() {
    // Check for promo_id in URL first
    this.handlePromoId()

    // Start loading data immediately (skeleton UI already initialized in created)
    this.loadHomepageDataWithSkeleton()

    // Start background preloading popular content after 5 seconds
    // This gives time for initial page load and user interaction
    setTimeout(() => {
      this.preloadPopularContent()
    }, 5000)

    // Listen for reset homepage state event from center button
    this.$root.$on('reset-homepage-state', this.handleNavigateHome)
  },
  beforeDestroy() {
    // Clean up event listener
    this.$root.$off('reset-homepage-state', this.handleNavigateHome)
  },
  methods: {
    // Get random items from array
    getRandomItems(array, count) {
      if (!array || array.length <= count) return array || []
      const shuffled = [...array].sort(() => Math.random() - 0.5)
      return shuffled.slice(0, count)
    },

    // New method for loading data with skeleton UI
    async loadHomepageDataWithSkeleton() {
      try {
        // Initialize cache strategies (still needed for menu data)
        this.initializeCacheStrategies()

        // Always fetch fresh data from API
        await this.fetchAndCacheHomepageDataWithSkeleton()
      } catch (error) {
        console.error('Failed to load homepage data:', error)
        // Hide skeleton even on error with minimum display time
        await this.hideSkeletonWithMinTime()
      }
    },

    // Original method for backward compatibility
    async loadHomepageData() {
      // Delegate to new skeleton-aware method
      await this.loadHomepageDataWithSkeleton()
    },

    // New method for fetching data with skeleton UI
    async fetchAndCacheHomepageDataWithSkeleton() {
      try {
        const response = await gameAPI.getHomepageData()
        console.log('Homepage API response:', response)

        const processedData = GameDataProcessor.processApiResponse(response)

        // Set homepage display data
        this.homepageGames = {
          bonusGames: processedData.bonusGames,
          newGames: processedData.newGames,
          newUserGames: processedData.newUserGames,
          hotPGGames: processedData.hotPGGames,
          popularGames: processedData.popularGames,
          // Unlimited versions for menu pages
          newGamesUnlimited: processedData.newGamesUnlimited || [],
          bonusGamesUnlimited: processedData.bonusGamesUnlimited || [],
          popularGamesUnlimited: processedData.popularGamesUnlimited || []
        }

        // Hide skeleton with minimum display time
        await this.hideSkeletonWithMinTime()

        console.log('Processed and cached homepage games:', this.homepageGames)
      } catch (error) {
        console.error('Failed to fetch homepage data:', error)
        // Hide skeleton even on error with minimum display time
        await this.hideSkeletonWithMinTime()
      }
    },

    // Original method for backward compatibility
    async fetchAndCacheHomepageData() {
      this.setLoading(
        true,
        LOADING_MESSAGES.HOMEPAGE.title,
        LOADING_MESSAGES.HOMEPAGE.subtitle
      )

      try {
        await this.fetchAndCacheHomepageDataWithSkeleton()
        this.setLoading(false)
      } catch (error) {
        this.setLoading(false)
        throw error
      }
    },

    // Cache homepage data using strategies
    cacheHomepageData(processedData) {
      // Cache homepage sections
      HOMEPAGE_SECTIONS.forEach(section => {
        if (processedData[section]) {
          const strategy = this.cacheStrategies[section]
          strategy.set(processedData[section])
        }
      })

      // Cache menu data
      MENU_CACHE_KEYS.forEach(key => {
        const menuData = GameDataProcessor.getCacheData(processedData, key)
        const strategy = this.cacheStrategies[key]
        strategy.setMenuData(menuData, processedData)
      })
    },

    // Initialize cache strategies
    initializeCacheStrategies() {
      if (Object.keys(this.cacheStrategies).length > 0) return

      // Initialize homepage section strategies
      HOMEPAGE_SECTIONS.forEach(section => {
        this.cacheStrategies[section] = CacheStrategyFactory.autoCreate(section)
      })

      // Initialize menu cache strategies
      MENU_CACHE_KEYS.forEach(key => {
        this.cacheStrategies[key] = CacheStrategyFactory.autoCreate(key)
      })

      // Initialize fishing cache strategy
      this.cacheStrategies.fishing = CacheStrategyFactory.autoCreate('fishing')
    },

    // Loading state management
    setLoading(loading, title = 'กำลังโหลด...', subtitle = 'กรุณารอสักครู่') {
      this.loading = loading
      this.loadingTitle = title
      this.loadingSubtitle = subtitle
    },

    // Generic provider loading method
    async loadProviders(keyName, type) {
      try {
        const response = await gameAPI.getGameProviders(type)
        if (response.success && response.data && response.data.lists) {
          this.providers[keyName] = response.data.lists
          console.log(
            `Loaded ${keyName} providers:`,
            this.providers[keyName].length
          )
        } else {
          console.error(`Invalid response format from ${keyName} providers API`)
          this.providers[keyName] = []
        }
        return { data: [] } // Return empty data since providers are stored separately
      } catch (error) {
        console.error(`Failed to fetch ${keyName} providers:`, error)
        this.providers[keyName] = []
        return { data: [] }
      }
    },

    // Slide handlers
    onSlideStart(slideIndex) {
      this.currentSlide = slideIndex
    },

    onSlideEnd(slideIndex) {
      this.currentSlide = slideIndex
    },

    // Simple direct keyName-based selection
    async handleSelectionChange(selection) {
      // Simple direct flow: keyName -> fetchData
      await this.fetchDataByKeyName(selection.keyName)
    },

    // Unified data fetching by keyName using cache strategies
    async fetchDataByKeyName(keyName, silent = false) {
      try {
        // Initialize cache strategies if needed
        this.initializeCacheStrategies()

        // Check strategy cache first
        const strategy = this.cacheStrategies[keyName]
        if (strategy && strategy.isValid()) {
          const cached = strategy.get()
          if (cached) {
            if (!silent) {
              this.showGameContent(keyName, cached)
            }
            return cached
          }
        }

        // Show loading UI only if not silent
        if (!silent) {
          this.setLoading(
            true,
            `กำลังโหลด${MENU_TITLES[keyName]}...`,
            LOADING_MESSAGES.MENU_CONTENT.title
          )
        }

        // Determine API call based on keyName
        let response
        if (
          keyName === 'new-games' ||
          keyName === 'hot-games' ||
          keyName === 'popular-games'
        ) {
          // For menu keys, ensure homepage data is loaded first
          await this.loadHomepageData()

          // Map keyName to unlimited homepageGames (show all games for menu pages)
          const menuDataMap = {
            'new-games': this.homepageGames.newGamesUnlimited,
            'hot-games': this.homepageGames.bonusGamesUnlimited,
            'popular-games': this.homepageGames.popularGamesUnlimited
          }
          const menuData = menuDataMap[keyName] || []

          if (!silent) {
            this.showGameContent(keyName, menuData)
            this.setLoading(false)
          }
          return menuData
        }

        if (['slots', 'baccarat', 'sport', 'lottery'].includes(keyName)) {
          // Provider-based games from real API
          const providerTypeMap = {
            slots: 'SLOT',
            baccarat: 'CASINO',
            sport: ['SBO', 'LALI', 'SABA', 'OBX', 'FBS', 'DBS', 'AFB'],
            lottery: 'AML'
          }
          response = await this.loadProviders(keyName, providerTypeMap[keyName])
        } else if (keyName === 'fishing') {
          // Fishing games from real API
          response = await gameAPI.getFishingGames()
        } else {
          // Other game types - placeholder for future
          response = { data: [] }
        }

        // Cache the result using strategy
        if (strategy && response?.data) {
          strategy.set(response.data)
        }

        // Show content and hide loading only if not silent
        if (!silent) {
          this.showGameContent(keyName, response.data)
          this.setLoading(false)
        }

        return response.data
      } catch (error) {
        console.error('Failed to load content for keyName:', keyName, error)
        if (!silent) {
          this.setLoading(false)
        }
        return null
      }
    },

    // Show game content (replaces both showCategoryContent and showGameTypeContent)
    showGameContent(keyName, games) {
      this.activeMenu = 'gameSelected'
      this.selectedKeyName = keyName
      this.currentContent = games
      this.selectedMenuItem = {
        keyName,
        title: MENU_TITLES[keyName]
      }
    },

    // Return to homepage
    showHomepage() {
      this.activeMenu = 'homepage'
      this.selectedKeyName = null
      this.currentContent = []
      this.selectedMenuItem = null
      // Don't show skeleton when returning to homepage if data is already loaded
      // Skeleton state is controlled by dataLoaded flags
    },

    handleGameSelect(game) {
      // Game launch is now handled directly by GameCard component
      // This method can be used for analytics or other tracking if needed
      console.log('Game launched:', game.gameName, 'by', game.provider)
    },

    handleProviderSelect(provider) {
      // Handle provider selection
      console.log('Provider selected:', provider)
      // Add logic to navigate to provider games or open provider modal
      // Example: this.$router.push(`/provider/${provider.provider}/games`)
    },

    handleProviderViewStateChange(viewState) {
      // Track provider view state for header visibility management
      this.providerViewState = viewState
      console.log('Provider view state changed:', viewState)
    },

    // Breadcrumb navigation
    handleNavigateHome() {
      // Navigate back to homepage
      this.activeMenu = 'homepage'
      this.selectedKeyName = null
      // Don't show skeleton when navigating home if data is already loaded
    },

    // Search methods
    toggleSearch() {
      if (this.searchExpanded) {
        this.closeSearch()
      } else {
        this.searchExpanded = true
        this.$nextTick(() => {
          if (this.$refs.searchInput) {
            this.$refs.searchInput.focus()
          }
        })
      }
    },

    closeSearch() {
      this.searchExpanded = false
      this.searchQuery = ''
    },

    // Helper method to hide all skeletons
    hideAllSkeletons() {
      this.dataLoaded.banner = true
      this.dataLoaded.menu = true
      this.dataLoaded.homepageGames = true
    },

    // Hide skeleton with minimum display time
    async hideSkeletonWithMinTime() {
      if (!this.skeletonStartTime) {
        this.hideAllSkeletons()
        return
      }

      const elapsed = Date.now() - this.skeletonStartTime
      const remaining = this.minimumSkeletonTime - elapsed

      if (remaining > 0) {
        // Wait for remaining time before hiding skeleton
        await new Promise(resolve => setTimeout(resolve, remaining))
      }

      this.hideAllSkeletons()
    },

    // Background preloading for better UX
    async preloadPopularContent() {
      try {
        // Most popular sections to preload
        const popularKeyNames = ['slots', 'baccarat']

        // Use Promise.all to preload in parallel instead of sequential await in loop
        const preloadPromises = popularKeyNames
          .filter(keyName => {
            const strategy = this.cacheStrategies[keyName]
            return !strategy || !strategy.isValid()
          })
          .map(async keyName => {
            await this.fetchDataByKeyName(keyName, true) // silent mode
            console.log(`Preloaded ${keyName} content for better UX`)
          })

        await Promise.all(preloadPromises)
      } catch (error) {
        // Silent fail for background preloading - don't interrupt user experience
        console.log(
          'Background preloading completed with some errors (normal behavior)'
        )
      }
    },

    // Handle promo_id URL parameter
    handlePromoId() {
      const urlParams = new URLSearchParams(window.location.search)
      const promoId = urlParams.get('promo_id')
      const cid = urlParams.get('cid')
      // Natural/organic option: opens the modal with no promo/ad attribution
      const register = urlParams.get('register')

      // Clean promo_id and register from URL (keep cid in URL)
      if (promoId || register) {
        const url = new URL(window.location.href)
        url.searchParams.delete('promo_id')
        url.searchParams.delete('register')
        window.history.replaceState({}, '', url.pathname + url.search)
      }

      // Only show popup for non-logged-in users
      if ((promoId || cid || register) && !isUserLoggedIn()) {
        if (promoId) {
          // Store in localStorage with 1 hour expiry (as backup)
          setPromoId(promoId)
        }

        // Trigger register popup via EventBus, passing promoId directly
        // This ensures promoId is available even if localStorage fails
        this.$root.$emit('show-register-modal', { promoId })
      }
    }
  }
}
</script>

<style lang="scss" scoped>
.home-page {
  min-height: 100vh;
  position: relative;

  // Ensure skeleton components render immediately without flicker
  .hero-section,
  .game-menu-section {
    min-height: 100px; // Prevent layout shift
    display: block; // Ensure immediate display
  }
}

// Fixed header section
.header-section {
  position: relative;
  z-index: 2;
}

.hero-section {
  margin-bottom: 30px;

  @media (max-width: 768px) {
    margin-bottom: 20px;
  }
}

.game-menu-section {
  margin-bottom: 20px;
  min-height: 120px; // Prevent collapse during initialization

  @media (max-width: 768px) {
    margin-bottom: 10px;
    min-height: 100px;
  }

  @media (max-width: 480px) {
    min-height: 80px;
  }
}

// Dynamic content section
.content-section {
  position: relative;
  min-height: 400px;

  @media (max-width: 768px) {
    min-height: 300px;
  }
}

.homepage-content {
  padding: 0;
  min-height: 400px; // Prevent collapse during initialization

  @media (max-width: 768px) {
    min-height: 350px;
  }

  @media (max-width: 480px) {
    min-height: 300px;
  }
}

.game-content {
  position: relative;
  padding: 20px 0;

  @media (max-width: 768px) {
    padding: 15px 0;
  }
}

.content-header {
  padding: 0 10px 10px;

  @media (max-width: 768px) {
    padding: 0 15px 15px;
  }
}

.header-row {
  display: flex;
  align-items: center;
  gap: 20px;
  width: 100%;

  @media (max-width: 768px) {
    gap: 10px;
  }
}

.title-section {
  flex: 1 1 50%;
  min-width: 0;
}

.content-title {
  color: #fff;
  font-size: clamp(1.8rem, 3vw, 2.5rem);
  font-weight: 400;
  margin-bottom: 10px;
  text-shadow: 0 2px 10px rgba(0, 0, 0, 0.5);
}

.content-subtitle {
  color: rgba(255, 255, 255, 0.8);
  font-size: 1.1rem;
  font-weight: 300;
}

// Content transitions
.content-fade-enter-active,
.content-fade-leave-active {
  transition: all 0.3s ease;
}

.content-fade-enter,
.content-fade-leave-to {
  opacity: 0;
  transform: translateY(10px);
}

// Skeleton to content transitions
.header-section,
.content-section {
  transition: opacity 0.3s ease-in-out;
}

// Smooth skeleton transitions
.hero-section > *,
.game-menu-section > *,
.homepage-content > * {
  transition: opacity 0.25s ease-in-out;
}

// Responsive adjustments
@media (max-width: 768px) {
  .content-header {
    padding: 0 15px 15px;
  }

  .content-title {
    font-size: 1.5rem;
  }

  .content-subtitle {
    font-size: 1rem;
  }
}

@media (max-width: 480px) {
  .hero-section {
    margin-bottom: 15px;
  }

  .game-menu-section {
    margin-bottom: 8px;
  }

  .content-header {
    padding: 0 12px 12px;
  }
}
</style>
